// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — Settings Page
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { useSnapshot } from 'valtio';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { store } from '@/lib/store';
import { api } from '@/lib/api';
import { Card, PageHeader, Button, Input, Badge } from '@/components/ui';
import {
  User,
  Shield,
  Database,
  Globe,
  Key,
  Save,
  Github,
  Mail,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Send,
  Unplug,
  Wifi,
  WifiOff,
} from 'lucide-react';
import toast from 'react-hot-toast';

const MotionCard = motion.create(Card);

export default function SettingsPage() {
  const { t } = useTranslation();
  const snap = useSnapshot(store);
  const [aiApiKey, setAiApiKey] = useState('');
  const [githubToken, setGithubToken] = useState('');
  const [testEmailTo, setTestEmailTo] = useState('');

  const { data: emailStatus } = useQuery({
    queryKey: ['email-status'],
    queryFn: () => api.getEmailStatus(),
  });

  const { data: githubStatus, refetch: refetchGithub } = useQuery({
    queryKey: ['github-status'],
    queryFn: () => api.getGithubUserStatus(),
  });

  const sendTestEmail = useMutation({
    mutationFn: (to: string) => api.sendTestEmail(to),
    onSuccess: () => toast.success(t('settings.testEmailSent')),
    onError: (err: Error) => toast.error(err.message),
  });

  const connectGithub = useMutation({
    mutationFn: () => api.connectGithub(),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const saveGithubToken = useMutation({
    mutationFn: (token: string) => api.saveGithubToken(token),
    onSuccess: () => {
      toast.success(t('settings.githubTokenSaved'));
      setGithubToken('');
      refetchGithub();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const disconnectGithub = useMutation({
    mutationFn: () => api.disconnectGithub(0),
    onSuccess: () => {
      toast.success(t('settings.githubDisconnected'));
      refetchGithub();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const animItem = (i: number) => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: i * 0.1, duration: 0.4 },
  });

  return (
    <div className="p-8">
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl">
        {/* Profile */}
        <MotionCard {...animItem(0)}>
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-brand-400" />
            {t('settings.profile')}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-brand-500/25">
                {snap.user?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <div className="text-lg font-semibold text-surface-100">
                  {snap.user?.name || ''}
                </div>
                <div className="text-sm text-surface-400">{snap.user?.email || ''}</div>
                <div className="flex items-center gap-2 mt-1">
                  <Shield className="w-3.5 h-3.5 text-brand-400" />
                  <Badge variant="brand">{snap.user?.role || 'member'}</Badge>
                </div>
              </div>
            </div>
            <Input label={t('settings.name')} defaultValue={snap.user?.name || ''} />
            <Input label={t('settings.email')} defaultValue={snap.user?.email || ''} disabled />
            <Button variant="secondary">
              <Save className="w-4 h-4" />
              {t('settings.updateProfile')}
            </Button>
          </div>
        </MotionCard>

        {/* GitHub Integration */}
        <MotionCard {...animItem(1)}>
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <Github className="w-5 h-5 text-brand-400" />
            {t('settings.github')}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40 border border-surface-700/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-surface-800 flex items-center justify-center">
                  <Github className="w-5 h-5 text-surface-300" />
                </div>
                <div>
                  <div className="text-sm font-medium text-surface-200">
                    {githubStatus?.connected
                      ? t('settings.githubConnected', { login: githubStatus.githubUser?.login })
                      : t('settings.githubConnection')}
                  </div>
                  <div className="text-xs text-surface-500">{t('settings.githubDesc')}</div>
                </div>
              </div>
              <Badge variant={githubStatus?.connected ? 'success' : 'outline'}>
                {githubStatus?.connected ? (
                  <>
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {t('common.connected')}
                  </>
                ) : (
                  <>
                    <Unplug className="w-3 h-3 mr-1" />
                    {t('settings.notConnected')}
                  </>
                )}
              </Badge>
            </div>
            {githubStatus?.connected ? (
              <Button
                variant="danger"
                onClick={() => disconnectGithub.mutate()}
                loading={disconnectGithub.isPending}
                className="w-full"
              >
                <Unplug className="w-4 h-4" />
                {t('settings.disconnectGithub')}
              </Button>
            ) : githubStatus?.oauthConfigured ? (
              <Button
                variant="secondary"
                loading={connectGithub.isPending}
                onClick={() => connectGithub.mutate()}
                className="w-full"
              >
                <Github className="w-4 h-4" />
                {t('settings.connectGithub')}
                <ExternalLink className="w-3.5 h-3.5 ml-auto" />
              </Button>
            ) : null}
            <div className="border-t border-surface-800 pt-4 mt-4">
              <Input
                label={t('settings.githubTokenLabel')}
                type="password"
                placeholder={t('settings.githubTokenPlaceholder')}
                value={githubToken}
                onChange={(e) => setGithubToken(e.target.value)}
              />
              <p className="text-xs text-surface-500 mt-1">{t('settings.githubTokenHint')}</p>
              {githubStatus?.connected && (
                <div className="mt-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
                  <p className="text-xs text-warning">
                    <strong>Fine-grained PAT?</strong> If you see fewer repos than expected, your
                    token was created with “Only select repositories”. Re-create it with{' '}
                    <strong>“All repositories”</strong> access under Repository Permissions:{' '}
                    <em>Contents</em> (Read), <em>Metadata</em> (Read).
                  </p>
                </div>
              )}
              <Button
                variant="secondary"
                className="mt-3 w-full"
                loading={saveGithubToken.isPending}
                disabled={!githubToken}
                onClick={() => saveGithubToken.mutate(githubToken)}
              >
                <Save className="w-4 h-4" />
                {t('settings.saveToken')}
              </Button>
            </div>
          </div>
        </MotionCard>

        {/* Email / SMTP */}
        <MotionCard {...animItem(2)}>
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-brand-400" />
            {t('settings.email')}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40 border border-surface-700/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-surface-800 flex items-center justify-center">
                  {emailStatus?.connected ? (
                    <CheckCircle2 className="w-5 h-5 text-success" />
                  ) : (
                    <XCircle className="w-5 h-5 text-danger" />
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium text-surface-200">
                    {t('settings.smtpStatus')}
                  </div>
                  <div className="text-xs text-surface-500">smtp.hostinger.com:465</div>
                </div>
              </div>
              <Badge variant={emailStatus?.connected ? 'success' : 'danger'}>
                {emailStatus?.connected ? (
                  <>
                    <Wifi className="w-3 h-3 mr-1" /> {t('common.connected')}
                  </>
                ) : (
                  <>
                    <WifiOff className="w-3 h-3 mr-1" /> {t('common.disconnected')}
                  </>
                )}
              </Badge>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30">
                <span className="text-surface-400">{t('settings.smtpFrom')}</span>
                <span className="text-surface-200 font-mono">contact@aram-services.com</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30">
                <span className="text-surface-400">{t('settings.smtpTransport')}</span>
                <span className="text-surface-200 font-mono">TLS / Port 465</span>
              </div>
            </div>
            <div className="border-t border-surface-800 pt-4">
              <label className="text-sm font-medium text-surface-300 block mb-2">
                {t('settings.sendTestEmail')}
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder={t('settings.testEmailPlaceholder')}
                  value={testEmailTo}
                  onChange={(e) => setTestEmailTo(e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="secondary"
                  loading={sendTestEmail.isPending}
                  onClick={() => {
                    if (testEmailTo) sendTestEmail.mutate(testEmailTo);
                  }}
                  disabled={!testEmailTo}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </MotionCard>

        {/* API Keys & AI Models */}
        <MotionCard {...animItem(3)}>
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-brand-400" />
            {t('settings.apiKeys')}
          </h3>

          {/* Platform-provided model notice */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10 mb-4">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-surface-200">
                {t('settings.platformProvidesAi')}
              </p>
              <p className="text-xs text-surface-400">{t('settings.platformProvidesAiDesc')}</p>
            </div>
          </div>

          {/* Optional personal key */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-surface-300 mb-1.5 flex items-center gap-1">
                {t('settings.personalApiKeyLabel')}
                <span className="text-xs text-surface-500 ml-1">({t('common.optional')})</span>
              </label>
              <p className="text-xs text-surface-500 mb-2">{t('settings.personalApiKeyDesc')}</p>
              <Input
                type="password"
                placeholder={t('settings.personalApiKeyPlaceholder')}
                value={aiApiKey}
                onChange={(e) => setAiApiKey(e.target.value)}
              />
            </div>
            <Button variant="secondary" onClick={() => toast.success(t('settings.keysSavedToast'))}>
              <Save className="w-4 h-4" />
              {t('settings.saveKeys')}
            </Button>
          </div>
        </MotionCard>

        {/* Database */}
        <MotionCard {...animItem(4)}>
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-brand-400" />
            {t('settings.database')}
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30">
              <span className="text-surface-400">{t('settings.dbHost')}</span>
              <span className="text-surface-200 font-mono">localhost</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30">
              <span className="text-surface-400">{t('settings.dbPort')}</span>
              <span className="text-surface-200 font-mono">5434</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30">
              <span className="text-surface-400">{t('settings.dbName')}</span>
              <span className="text-surface-200 font-mono">arams_platform</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30">
              <span className="text-surface-400">{t('settings.dbStatus')}</span>
              <span className="text-success font-medium">{t('common.connected')}</span>
            </div>
          </div>
        </MotionCard>

        {/* Platform Info */}
        <MotionCard {...animItem(5)}>
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-brand-400" />
            {t('settings.platformInfo')}
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30">
              <span className="text-surface-400">{t('settings.version')}</span>
              <span className="text-surface-200 font-mono">1.0.0</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30">
              <span className="text-surface-400">{t('settings.domain')}</span>
              <span className="text-surface-200 font-mono">&mdash;</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30">
              <span className="text-surface-400">{t('settings.stack')}</span>
              <span className="text-surface-200">{t('settings.stackValue')}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30">
              <span className="text-surface-400">{t('settings.aiEngine')}</span>
              <span className="text-surface-200">{t('settings.aiEngineValue')}</span>
            </div>
          </div>
        </MotionCard>
      </div>
    </div>
  );
}
